import { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { KpiDropdownActions, KpiType, StorePerformanceInterval } from '../../../../../constants';
import { SalesNetPerformanceItem, useKpiTargetDistribution, useUser } from '../../../../../hooks';
import { muiTheme } from '../../../../../theme';
import { homeTranslations, kpiTargetTranslations } from '../../../../../translations';
import { BudgetPlannedContent } from '../../../common/budgetPlannedContent/BudgetPlannedContent';
import { formatAmountWithSeparator, getKpiTargetProps, getKpiTargetValue } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { Icon, Icons, Typography } from '../../../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../../../stories/atoms/containerV2/ContainerV2';
import { DropdownMenu, DynamicTab } from '../../../../../stories/molecules';
import { useThemeContext } from '../../../../../global-state/themeContext';
import { isMultiDayInterval } from '../../../util';
import { ContainerDrawer } from '../../../../../stories/molecules/container-drawer/ContainerDrawer';
import { getDefaultStatus } from '../../../common/metricTile/util';
import { WidgetMetricTile, WidgetMetricType } from '../../../common/metricTile/WidgetMetricTile';
import { StyledTrendIconComponent } from '../../../common/styledTrendIcon/StyledTrendIcon';
import WidgetChart from '../../../common/widgetChart/WidgetChart';
import { WidgetMobileLayout } from '../../../common/widgetMobileLayout/WidgetMobileLayout';
import { useSalesNetWidgetData } from './useSalesNetWidgetData';
import {
  buildTabLabels,
  buildTargetModel,
  getActiveForecastItems,
  getIsNoDataByTab,
  getItemValueByTab,
  getShowForecastTile,
  getTargetProgressPercent,
  getTotalNetSales,
  salesNetTabs,
} from './util';
import { SalesNetDesktopContent } from './salesNetDesktopContent/SalesNetDesktopContent';

type SalesNetWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  hasPermissionToEditKpi: boolean;
  isToday: boolean;
  isKpiLoading: boolean;
  isKpiError: boolean;
  handleDropdownDetails: (kpiType: KpiType, action: KpiDropdownActions) => void;
  onTargetAchieved: (widgetName: string, target: string, lastUpdated: Date) => void;
};

export const SalesNetWidget: FC<SalesNetWidgetProps> = observer(
  ({
    interval,
    currentDate,
    hasPermissionToEditKpi,
    isToday,
    isKpiLoading,
    isKpiError,
    handleDropdownDetails,
    onTargetAchieved,
  }) => {
    const { mode } = useThemeContext();
    const { getKpiTargetDistribution } = useKpiTargetDistribution();
    const {
      get: { kpiTargetsProps },
    } = useUser();

    const { t, i18n } = useTranslation();

    const { hourlyTurnover, kpiTargets, budgetPlanned, common: commonTranslations } = homeTranslations;
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
    const { state, data, budget, format } = useSalesNetWidgetData({
      interval,
      currentDate,
      isToday,
      onTargetAchieved,
    });

    const {
      tabIndex,
      handleTabChange,
      isBudgetSliderOpen,
      setIsBudgetSliderOpen,
      activeTab,
      activeComparison,
      lastUpdated,
    } = state;
    const {
      performanceItems,
      performanceItemsLoading,
      performanceItemsError,
      filteredPerformanceItems,
      forecastPerformanceItems,
      forecastPerformanceItemsLoading,
      activeForecastApiName,
    } = data;
    const {
      netSaleBudgetPlanned,
      netSaleBudgetPlannedLoading,
      netSaleBudgetPlannedError,
      canViewBudget,
      canViewPlanned,
      showBudgetAndPlanned,
      retryWidgetData,
    } = budget;
    const { selectedCurrency, getFormattedCurrency, getConvertedAmount } = format;
    const editableTarget = getKpiTargetValue(kpiTargetsProps, 'dailyTurnOver');
    const isTargetLocked = getKpiTargetProps(kpiTargetsProps, 'dailyTurnOver')?.isLocked ?? false;
    const editableSCOTarget = getKpiTargetValue(kpiTargetsProps, 'dailySCOTurnOver');
    const isSCOTargetLocked = getKpiTargetProps(kpiTargetsProps, 'dailySCOTurnOver')?.isLocked ?? false;

    const tabLabels = buildTabLabels(
      salesNetTabs,
      t,
      (tab) => tab.id !== 'all' && getIsNoDataByTab(performanceItems, tab),
    );

    const targetModel = buildTargetModel(activeTab, kpiTargetsProps);
    const { targetValue: target, showTarget } = targetModel;

    const targetItems =
      targetModel.targetKpiType === null
        ? []
        : getKpiTargetDistribution({
            performanceItems,
            kpiName: targetModel.targetKpiType,
          });

    const convertedTargetItems = targetItems.map((item) => {
      return item ? getConvertedAmount(item) : null;
    });

    const totalNetSales = getTotalNetSales(performanceItems, activeTab.metricKey);

    const activeForecastItems = getActiveForecastItems(activeTab, activeForecastApiName, forecastPerformanceItems);

    const forecastTotalNetSales = getTotalNetSales(
      [...performanceItems, ...activeForecastItems],
      activeTab.metricKey,
    );

    const showForecast = getShowForecastTile({
      tab: activeTab,
      isForecastLoading: forecastPerformanceItemsLoading,
      targetValue: target,
      activeForecastItems,
    });

    const isTabViewNoData = activeTab.id !== 'all' && activeComparison.today === 0;
    const targetProgressPercent = getTargetProgressPercent(target, forecastTotalNetSales);

    const itemValueResolver = (item: SalesNetPerformanceItem) => {
      return getConvertedAmount(getItemValueByTab(item, activeTab));
    };

    const changeNote = isToday
      ? ''
      : t(commonTranslations.changeVsCurrentNote.key, commonTranslations.changeVsCurrentNote.defaultValue);

    const getTooltipText = () => {
      let translation = '';

      if (!showBudgetAndPlanned) {
        translation = t(
          budgetPlanned.noPermissionBudgetTooltip.key,
          budgetPlanned.noPermissionBudgetTooltip.defaultValue,
        );
      }

      return (
        <ContainerV2 padding={[1]}>
          <Typography color="white">{translation}</Typography>
        </ContainerV2>
      );
    };

    const isEditKPIDisabled = !hasPermissionToEditKpi;
    const isPreviousDay = isMultiDayInterval(interval) || !isToday;

    const getKpiTooltipText = (isKpiLocked: boolean) => {
      return (
        <ContainerV2 padding={[1]}>
          <Typography color="white">
            {!hasPermissionToEditKpi
              ? t(kpiTargets.noPermissionTooltip.key, kpiTargets.noPermissionTooltip.defaultValue)
              : isKpiLocked
              ? t(kpiTargets.lockedTargetTooltip.key, kpiTargets.lockedTargetTooltip.defaultValue)
              : isPreviousDay
              ? t(kpiTargets.previousDayTooltip.key, kpiTargets.previousDayTooltip.defaultValue)
              : t(kpiTargets.disabledTooltip.key, kpiTargets.disabledTooltip.defaultValue)}
          </Typography>
        </ContainerV2>
      );
    };

    const menuItems = [
      {
        key: 'editNetSalesKpi',
        disabled: isEditKPIDisabled || isTargetLocked || isPreviousDay,
        hasTooltip: isEditKPIDisabled || isTargetLocked || isPreviousDay,
        tooltipText: getKpiTooltipText(isTargetLocked),
        item: (
          <Typography margin={[0, 2, 0]}>
            {editableTarget
              ? t(hourlyTurnover.editKpiSalesNet.key, hourlyTurnover.editKpiSalesNet.defaultValue)
              : t(hourlyTurnover.addKpiSalesNet.key, hourlyTurnover.addKpiSalesNet.defaultValue)}
          </Typography>
        ),
      },
      {
        key: 'editSCOSalesKpi',
        disabled: isEditKPIDisabled || isSCOTargetLocked || isPreviousDay,
        hasTooltip: isEditKPIDisabled || isSCOTargetLocked || isPreviousDay,
        tooltipText: getKpiTooltipText(isSCOTargetLocked),
        item: (
          <Typography margin={[0, 2, 0]}>
            {editableSCOTarget
              ? t(hourlyTurnover.editKpiSCOSalesNet.key, hourlyTurnover.editKpiSCOSalesNet.defaultValue)
              : t(hourlyTurnover.addKpiSCOSalesNet.key, hourlyTurnover.addKpiSCOSalesNet.defaultValue)}
          </Typography>
        ),
      },
      {
        key: 'showBudget',
        disabled: !showBudgetAndPlanned,
        hasTooltip: !showBudgetAndPlanned,
        tooltipText: getTooltipText(),
        item: (
          <Typography margin={[0, 2, 0]}>
            {t(budgetPlanned.budgetDropdown.key, budgetPlanned.budgetDropdown.defaultValue)}
          </Typography>
        ),
      },
    ];

    const mobileMetricItems = [
      {
        label: t(hourlyTurnover.totalSales.key, hourlyTurnover.totalSales.defaultValue),
        content: getFormattedCurrency(totalNetSales),
        tooltipText: t(
          hourlyTurnover.metricTileTooltip.totalSalesTile.key,
          hourlyTurnover.metricTileTooltip.totalSalesTile.defaultValue,
        ),
      },
      ...(showTarget
        ? [
            {
              label: t(
                kpiTargetTranslations.kpiProperties.target.title.key,
                kpiTargetTranslations.kpiProperties.target.title.defaultValue,
              ),
              content: target === null ? `-` : getFormattedCurrency(target),
              isLoading: isKpiLoading,
              isError: isKpiError,
              tooltipText: t(
                hourlyTurnover.metricTileTooltip.targetTile.key,
                hourlyTurnover.metricTileTooltip.targetTile.defaultValue,
              ),
            },
          ]
        : []),
      {
        label: t(commonTranslations.lastWeek.key, commonTranslations.lastWeek.defaultValue),
        content: (
          <StyledTrendIconComponent
            originalAmount={activeComparison.today}
            previousAmount={activeComparison.lastWeek}
            direction="horizontal"
            valueFormatter={(value) => getFormattedCurrency(value)}
            textOptions={{ type: 'caption' }}
            hasPercentage={false}
            hideTrendText={isToday}
          />
        ),
        tooltipText: t(
          hourlyTurnover.metricTileTooltip.lastWeekTile.key,
          hourlyTurnover.metricTileTooltip.lastWeekTile.defaultValue,
          { changeNote },
        ),
      },
      {
        label: t(commonTranslations.lastYear.key, commonTranslations.lastYear.defaultValue),
        content: (
          <StyledTrendIconComponent
            originalAmount={activeComparison.today}
            previousAmount={activeComparison.lastYear}
            direction="horizontal"
            valueFormatter={(value) => getFormattedCurrency(value)}
            textOptions={{ type: 'caption' }}
            hasPercentage={false}
            hideTrendText={isToday}
          />
        ),
        tooltipText: t(
          hourlyTurnover.metricTileTooltip.lastYearTile.key,
          hourlyTurnover.metricTileTooltip.lastYearTile.defaultValue,
          { changeNote },
        ),
      },
    ];

    const isNoData = getTotalNetSales(performanceItems, 'netSales') === 0;

    const getTargetText = () => {
      const netSaleTarget = editableTarget
        ? getFormattedCurrency(editableTarget)
        : t(kpiTargetTranslations.notSet.key, kpiTargetTranslations.notSet.defaultValue);
      const SCOtarget = editableSCOTarget
        ? getFormattedCurrency(editableSCOTarget)
        : t(kpiTargetTranslations.notSet.key, kpiTargetTranslations.notSet.defaultValue);
      return `${t(hourlyTurnover.title.key, hourlyTurnover.title.defaultValue)} ${netSaleTarget} \n ${t(
        hourlyTurnover.tabs.scoSales.key,
        hourlyTurnover.tabs.scoSales.defaultValue,
      )} ${SCOtarget}`;
    };

    return (
      <ContainerDrawer
        isOpen={isBudgetSliderOpen}
        onClose={() => setIsBudgetSliderOpen(false)}
        sliderContent={
          <BudgetPlannedContent
            title={t(hourlyTurnover.title.key, hourlyTurnover.title.defaultValue)}
            currentData={activeComparison.today}
            isLoading={netSaleBudgetPlannedLoading}
            isError={netSaleBudgetPlannedError}
            canViewBudget={canViewBudget}
            canViewPlanned={canViewPlanned}
            budgetPlanned={netSaleBudgetPlanned}
            budgetNoData={netSaleBudgetPlanned?.budget === 0}
            plannedNoData={netSaleBudgetPlanned?.planned === 0}
            isCurrency
          />
        }
        width={isMobile ? '250px' : '300px'}
      >
        <WidgetCardContent
          headerIcon={Icons.SALESEFFICIENCY}
          headerText={t(hourlyTurnover.title.key, hourlyTurnover.title.defaultValue)}
          subHeaderText={t(hourlyTurnover.subTitle.key, hourlyTurnover.subTitle.defaultValue)}
          lastUpdatedDate={lastUpdated}
          isLoading={performanceItemsLoading}
          isError={performanceItemsError}
          errorText={t(hourlyTurnover.error.key, hourlyTurnover.error.defaultValue)}
          errorRetryHandler={retryWidgetData}
          isNoData={isNoData}
          target={showTarget ? getTargetText() : undefined}
          headerSideContent={
            <RowFlexContainer alignItems="center">
              <DropdownMenu
                data-testid="kpiDropdown"
                dropdownLabel={''}
                buttonContentProps={{
                  iconOptions: { customIcon: <Icon icon={Icons.MORE} /> },
                }}
                menuItems={menuItems}
                minWidth={'0px'}
                isCompact
                initialSelected={[]}
                onSelect={(selectedItems) => {
                  if (selectedItems[0] === 'editNetSalesKpi') {
                    handleDropdownDetails(
                      'dailyTurnOver',
                      editableTarget ? KpiDropdownActions.UPDATEKPI : KpiDropdownActions.ADDKPI,
                    );
                  } else if (selectedItems[0] === 'editSCOSalesKpi') {
                    handleDropdownDetails(
                      'dailySCOTurnOver',
                      editableSCOTarget ? KpiDropdownActions.UPDATEKPI : KpiDropdownActions.ADDKPI,
                    );
                  } else if (selectedItems[0] === 'showBudget') {
                    setIsBudgetSliderOpen(true);
                  }
                }}
                isOnlyIcon
                allowTooltip
              />
            </RowFlexContainer>
          }
          content={
            <ContainerV2 data-testid="sales-net-wrapper" height="100%">
              <DynamicTab
                tabLabelArray={tabLabels}
                handleTabChange={handleTabChange}
                tabValue={tabIndex}
                variant="scrollable"
                tabBgColor={mode === 'light' ? 'baseGray' : 'backgroundTableHeaderDark'}
                content={[]}
              />

              {isTabViewNoData && (
                <ColumnFlexContainer height="100%" justifyContent="center" alignItems="center">
                  <Typography type="button">
                    {t(commonTranslations.noReceipts.key, commonTranslations.noReceipts.defaultValue)}
                  </Typography>
                </ColumnFlexContainer>
              )}

              {!isTabViewNoData && !isMobile && (
                <ColumnFlexContainer height="100%" padding={[2]}>
                  <SalesNetDesktopContent
                    showForecast={showForecast}
                    totalNetSales={totalNetSales}
                    forecastTotalNetSales={forecastTotalNetSales}
                    targetProgressPercent={targetProgressPercent}
                    getFormattedCurrency={getFormattedCurrency}
                    isKpiLoading={isKpiLoading}
                    isKpiError={isKpiError}
                    isToday={isToday}
                    target={target}
                    showTarget={showTarget}
                    activeComparison={activeComparison}
                  />
                  <ContainerV2 data-testid="chart" flex={1}>
                    <ContainerV2 height="80%">
                      <WidgetChart
                        performanceItemsChartName={t(
                          hourlyTurnover.subTitle.key,
                          hourlyTurnover.subTitle.defaultValue,
                        )}
                        performanceItems={performanceItems}
                        filteredPerformanceItems={filteredPerformanceItems}
                        forecastPerformanceItems={activeForecastItems}
                        targetItems={showTarget ? convertedTargetItems : []}
                        itemValueResolver={itemValueResolver}
                        interval={interval}
                        showXAxis={true}
                        yAxisFormatter={(value) =>
                          `${Intl.NumberFormat(i18n.language, { notation: 'compact' }).format(value)}`
                        }
                        tooltipFormatter={(value) => formatAmountWithSeparator(value, selectedCurrency)}
                      />
                    </ContainerV2>
                  </ContainerV2>
                </ColumnFlexContainer>
              )}

              {!isTabViewNoData && isMobile && (
                <ContainerV2 padding={[2, 6, 0]} data-testid="chart" flex={0} minHeight={[0]}>
                  {showForecast && (
                    <WidgetMetricTile
                      metricProperties={{
                        type: WidgetMetricType.ForecastMetric,
                        properties: {
                          metricValue: getFormattedCurrency(forecastTotalNetSales),
                          forecastIndicator: getDefaultStatus(targetProgressPercent),
                          metricTooltipText: t(
                            commonTranslations.forecastTileTooltip.key,
                            commonTranslations.forecastTileTooltip.defaultValue,
                          ),
                        },
                      }}
                    />
                  )}

                  <WidgetMobileLayout
                    metricItems={mobileMetricItems}
                    renderChart={() => {
                      return (
                        <WidgetChart
                          performanceItemsChartName={t(
                            hourlyTurnover.subTitle.key,
                            hourlyTurnover.subTitle.defaultValue,
                          )}
                          performanceItems={performanceItems}
                          filteredPerformanceItems={filteredPerformanceItems}
                          forecastPerformanceItems={activeForecastItems}
                          targetItems={showTarget ? convertedTargetItems : []}
                          itemValueResolver={itemValueResolver}
                          interval={interval}
                          showXAxis={true}
                          showXAxisTooltip
                          yAxisFormatter={(value) =>
                            `${Intl.NumberFormat(i18n.language, { notation: 'compact' }).format(value)}`
                          }
                          tooltipFormatter={(value) => formatAmountWithSeparator(value, selectedCurrency)}
                          sparklineEnabled
                          customYAxis={{ show: false }}
                        />
                      );
                    }}
                  />
                </ContainerV2>
              )}
            </ContainerV2>
          }
          toolTipText={t(hourlyTurnover.toolTip.key, hourlyTurnover.toolTip.defaultValue)}
        />
      </ContainerDrawer>
    );
  },
);
